import { useState, useEffect } from 'react'
import {
    Plus,
    Search,
    Edit3,
    Trash2,
    X,
    Calendar,
    Home,
    User,
    DollarSign,
    Save,
    AlertCircle,
    AlertTriangle
} from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
// import { formatCurrency } from '../utils/formatCurrency' // Removido - não utilizado
import { CurrencyInputV2 } from '../components/CurrencyInputV2'
import { ComissoesLightbox } from '../components/ComissoesLightbox'
import { useModal } from '../contexts/ModalContext'
import { useToast } from '../hooks/useToast'
import { ToastContainer } from '../components/Toast'
import Button from '../components/Button'

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

// Interfaces baseadas no schema
interface Venda {
    id: string
    imovel_id: string
    unidade: string
    corretor_id: string
    cliente_nome: string
    valor_venda: number
    data_venda: string
    valor_comissao: number
    valor_bonus: number
    previsao_recebimento: string
    observacoes: string
    created_at: string
    // Dados relacionados (joins)
    imovel?: {
        nome: string
        endereco: string
        valor_venda: number
        tipo_imovel: string
        construtora_id?: string
        proprietario_id?: string
    }
    corretor?: {
        nome: string
        email: string
        telefone?: string
        creci?: string
    }
}

interface Imovel {
    id: string
    nome: string
    endereco: string
    valor_venda: number
    tipo: string
    tipo_imovel: string
    unidades_disponiveis: number
    unidades_vendidas: number
    construtora_id?: string
    proprietario_id?: string
}

interface Corretor {
    id: string
    nome: string
    email: string
    telefone?: string
    creci?: string
    ativo: boolean
}

// Interface Cliente removida - não utilizada

interface Construtora {
    id: string
    nome: string
    cnpj: string
    contato_responsavel?: string
}

interface Proprietario {
    id: string
    nome: string
    estado: string
    cidade: string
    telefone?: string
    email?: string
}

interface TipoImovel {
    id: string
    nome: string
    created_at: string
}

export default function Vendas() {
    const [vendas, setVendas] = useState<Venda[]>([])
    const [imoveis, setImoveis] = useState<Imovel[]>([])
    const [corretores, setCorretores] = useState<Corretor[]>([])
    const [construtoras, setConstrutoras] = useState<Construtora[]>([])
    const [proprietarios, setProprietarios] = useState<Proprietario[]>([])
    const [tiposImoveis, setTiposImoveis] = useState<TipoImovel[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingVenda, setEditingVenda] = useState<Venda | null>(null)
    const [isViewMode, setIsViewMode] = useState(false)
    const [formData, setFormData] = useState({
        imovel_id: '',
        unidade: '',
        corretor_id: '',
        cliente_nome: '',
        valor_venda: '',
        data_venda: '',
        valor_comissao: '',
        valor_bonus: '',
        previsao_recebimento: '',
        observacoes: '',
        tipo_imovel: 'novo',
        tipo_id: '',
        construtora_id: '',
        proprietario_id: ''
    })
    const [error, setError] = useState('')
    const [isComissoesLightboxOpen, setIsComissoesLightboxOpen] = useState(false)
    const [selectedVenda, setSelectedVenda] = useState<Venda | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [vendaToDelete, setVendaToDelete] = useState<Venda | null>(null)
    const { setIsModalOpen: setGlobalModalOpen } = useModal()
    const { toasts, showSuccess, showError } = useToast()

    // Carregar dados
    useEffect(() => {
        loadVendas()
        loadImoveis()
        loadCorretores()
        loadConstrutoras()
        loadProprietarios()
        loadTiposImoveis()
    }, [])

    // Auto-selecionar corretor quando carregado
    useEffect(() => {
        console.log('Corretores carregados:', corretores)
        console.log('FormData corretor_id atual:', formData.corretor_id)

        if (corretores.length === 1 && !formData.corretor_id) {
            console.log('Auto-selecionando corretor:', corretores[0].id)
            setFormData(prev => ({ ...prev, corretor_id: corretores[0].id }))
        }
    }, [corretores, formData.corretor_id])

    const loadVendas = async () => {
        try {
            console.log('🔄 Iniciando carregamento de vendas...')

            const { data, error } = await supabase
                .from('vendas')
                .select(`
          id,
          imovel_id,
          unidade,
          corretor_id,
          cliente_nome,
          valor_venda,
          data_venda,
          valor_comissao,
          valor_bonus,
          previsao_recebimento,
          observacoes,
          created_at,
          imovel:imoveis!inner (
            nome,
            endereco,
            valor_venda,
            tipo,
            tipo_imovel,
            construtora_id,
            proprietario_id
          ),
          corretor:corretores!inner (
            nome,
            email,
            telefone,
            creci
          )
        `)
                .order('created_at', { ascending: false })

            console.log('📊 Resultado da query vendas:', { data, error })

            if (error) {
                console.error('❌ Erro na query vendas:', error)
                throw error
            }

            console.log('✅ Vendas carregadas com sucesso:', data?.length || 0, 'registros')

            // Transformar os dados para o formato esperado
            const vendasTransformadas = (data || []).map((venda: any) => ({
                ...venda,
                imovel: Array.isArray(venda.imovel) ? venda.imovel[0] : venda.imovel,
                corretor: Array.isArray(venda.corretor) ? venda.corretor[0] : venda.corretor
            }))

            setVendas(vendasTransformadas)
        } catch (err) {
            console.error('❌ Erro ao carregar vendas:', err)
            setError(`Erro ao carregar vendas: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
        } finally {
            setLoading(false)
        }
    }

    const loadImoveis = async () => {
        try {
            console.log('🔄 Iniciando carregamento de imóveis...')

            const { data, error } = await supabase
                .from('imoveis')
                .select(`
          id,
          nome,
          endereco,
          valor_venda,
          tipo,
          tipo_imovel,
          unidades_disponiveis,
          unidades_vendidas,
          construtora_id,
          proprietario_id
        `)
                .order('nome', { ascending: true })

            console.log('📊 Resultado da query imóveis:', { data, error })

            if (error) {
                console.error('❌ Erro na query imóveis:', error)
                throw error
            }

            console.log('✅ Imóveis carregados com sucesso:', data?.length || 0, 'registros')
            setImoveis(data || [])
        } catch (err) {
            console.error('❌ Erro ao carregar imóveis:', err)
        }
    }

    const loadCorretores = async () => {
        try {
            console.log('Carregando corretor...')

            // Obter usuário logado
            const { data: { user }, error: authError } = await supabase.auth.getUser()

            console.log('Usuário autenticado:', user?.id, user?.email)

            if (authError || !user) {
                console.error('Usuário não autenticado:', authError)
                setCorretores([])
                return
            }

            // Buscar apenas o corretor do usuário logado
            const { data, error } = await supabase
                .from('corretores')
                .select('id, nome, email, telefone, creci, ativo')
                .eq('user_id', user.id)
                .eq('ativo', true)
                .single()

            console.log('Resultado da busca do corretor:', { data, error })

            if (error) {
                console.error('Erro ao buscar corretor:', error)
                setCorretores([])
                return
            }

            // Converter para array para manter compatibilidade com o resto do código
            setCorretores(data ? [data] : [])
            console.log('Corretor definido:', data ? [data] : [])
        } catch (err) {
            console.error('Erro ao carregar corretor:', err)
            setCorretores([])
        }
    }


    const loadConstrutoras = async () => {
        try {
            const { data, error } = await supabase
                .from('construtoras')
                .select('id, nome, cnpj, contato_responsavel')
                .order('nome', { ascending: true })

            if (error) throw error
            setConstrutoras(data || [])
        } catch (err) {
            console.error('Erro ao carregar construtoras:', err)
        }
    }

    const loadProprietarios = async () => {
        try {
            const { data, error } = await supabase
                .from('proprietarios')
                .select('id, nome, estado, cidade, telefone, email')
                .order('nome', { ascending: true })

            if (error) throw error
            setProprietarios(data || [])
        } catch (err) {
            console.error('Erro ao carregar proprietários:', err)
        }
    }

    const loadTiposImoveis = async () => {
        try {
            const { data, error } = await supabase
                .from('tipos_imoveis')
                .select('id, nome, created_at')
                .order('nome', { ascending: true })

            if (error) throw error
            setTiposImoveis(data || [])
        } catch (err) {
            console.error('Erro ao carregar tipos de imóveis:', err)
        }
    }

    // Função auxiliar para encontrar o tipo_id baseado no nome do tipo
    const getTipoIdByName = (tipoNome: string | undefined): string => {
        if (!tipoNome) return ''
        const tipo = tiposImoveis.find(t => t.nome === tipoNome)
        console.log('🔍 Buscando tipo_id para:', tipoNome, 'Encontrado:', tipo?.id)
        return tipo?.id || ''
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        // Validar se o corretor foi carregado
        if (!formData.corretor_id) {
            setError('Erro: Usuário não autenticado. Faça login novamente.')
            return
        }

        // Validar se o nome do cliente foi preenchido
        if (!formData.cliente_nome.trim()) {
            setError('Erro: Digite o nome do cliente.')
            return
        }

        // Validar se o tipo de imóvel foi selecionado
        if (!formData.tipo_id) {
            setError('Erro: Selecione o tipo de imóvel.')
            return
        }

        try {
            if (editingVenda) {
                // Atualizar venda existente
                const { error } = await supabase
                    .from('vendas')
                    .update({
                        imovel_id: formData.imovel_id,
                        unidade: formData.unidade,
                        corretor_id: formData.corretor_id,
                        cliente_nome: formData.cliente_nome,
                        valor_venda: parseFloat(formData.valor_venda),
                        data_venda: formData.data_venda,
                        valor_comissao: parseFloat(formData.valor_comissao) || 0,
                        valor_bonus: parseFloat(formData.valor_bonus) || 0,
                        previsao_recebimento: formData.previsao_recebimento || null,
                        observacoes: formData.observacoes || null
                    })
                    .eq('id', editingVenda.id)

                if (error) throw error

                // Atualizar tipo do imóvel se necessário
                const imovelSelecionado = imoveis.find(imovel => imovel.id === formData.imovel_id)
                if (imovelSelecionado && (imovelSelecionado as any).tipo_id !== formData.tipo_id) {
                    await supabase
                        .from('imoveis')
                        .update({
                            tipo_id: formData.tipo_id
                        })
                        .eq('id', formData.imovel_id)
                }
            } else {
                // Criar nova venda
                const { error } = await supabase
                    .from('vendas')
                    .insert({
                        imovel_id: formData.imovel_id,
                        unidade: formData.unidade,
                        corretor_id: formData.corretor_id,
                        cliente_nome: formData.cliente_nome,
                        valor_venda: parseFloat(formData.valor_venda),
                        data_venda: formData.data_venda,
                        valor_comissao: parseFloat(formData.valor_comissao) || 0,
                        valor_bonus: parseFloat(formData.valor_bonus) || 0,
                        previsao_recebimento: formData.previsao_recebimento || null,
                        observacoes: formData.observacoes || null
                    })

                if (error) throw error

                // Atualizar contadores de unidades do imóvel e associar tipo
                const imovelSelecionado = imoveis.find(imovel => imovel.id === formData.imovel_id)
                if (imovelSelecionado) {
                    await supabase
                        .from('imoveis')
                        .update({
                            unidades_vendidas: imovelSelecionado.unidades_vendidas + 1,
                            unidades_disponiveis: Math.max(0, imovelSelecionado.unidades_disponiveis - 1),
                            tipo_id: formData.tipo_id
                        })
                        .eq('id', formData.imovel_id)
                }
            }

            setIsModalOpen(false)
            setGlobalModalOpen(false)
            setEditingVenda(null)
            setFormData({
                imovel_id: '',
                unidade: '',
                corretor_id: '',
                cliente_nome: '',
                valor_venda: '',
                data_venda: '',
                valor_comissao: '',
                valor_bonus: '',
                previsao_recebimento: '',
                observacoes: '',
                tipo_imovel: 'novo',
                tipo_id: '',
                construtora_id: '',
                proprietario_id: ''
            })
            loadVendas()
            loadImoveis() // Recarregar imóveis para atualizar contadores
        } catch (err: any) {
            setError(err.message || 'Erro ao salvar venda')
        }
    }

    const handleEdit = (venda: Venda) => {
        console.log('✏️ Dados da venda para edição:', venda)
        console.log('📝 cliente_nome da venda:', venda.cliente_nome)
        console.log('🏠 tipo do imóvel:', (venda.imovel as any)?.tipo)
        console.log('📋 tipos disponíveis:', tiposImoveis)

        const tipoId = getTipoIdByName((venda.imovel as any)?.tipo)
        console.log('🎯 tipo_id encontrado:', tipoId)

        setEditingVenda(venda)
        setFormData({
            imovel_id: venda.imovel_id,
            unidade: venda.unidade,
            corretor_id: venda.corretor_id,
            cliente_nome: venda.cliente_nome || '',
            valor_venda: venda.valor_venda.toString(),
            data_venda: venda.data_venda,
            valor_comissao: venda.valor_comissao?.toString() || '',
            valor_bonus: venda.valor_bonus?.toString() || '',
            previsao_recebimento: venda.previsao_recebimento || '',
            observacoes: venda.observacoes || '',
            tipo_imovel: venda.imovel?.tipo_imovel || 'novo',
            tipo_id: tipoId,
            construtora_id: venda.imovel?.construtora_id || '',
            proprietario_id: venda.imovel?.proprietario_id || ''
        })
        setIsViewMode(false)
        setIsModalOpen(true)
        setGlobalModalOpen(true)
    }

    const handleDelete = (venda: Venda) => {
        setVendaToDelete(venda)
        setIsDeleteModalOpen(true)
        setGlobalModalOpen(true)
    }

    const confirmDelete = async () => {
        if (!vendaToDelete) return

        try {
            // Buscar a venda para pegar o imovel_id
            const { data: vendaData } = await supabase
                .from('vendas')
                .select('imovel_id')
                .eq('id', vendaToDelete.id)
                .single()

            // Excluir a venda
            const { error } = await supabase
                .from('vendas')
                .delete()
                .eq('id', vendaToDelete.id)

            if (error) throw error

            // Atualizar contadores de unidades do imóvel
            if (vendaData) {
                const imovelSelecionado = imoveis.find(imovel => imovel.id === vendaData.imovel_id)
                if (imovelSelecionado) {
                    await supabase
                        .from('imoveis')
                        .update({
                            unidades_vendidas: Math.max(0, imovelSelecionado.unidades_vendidas - 1),
                            unidades_disponiveis: imovelSelecionado.unidades_disponiveis + 1
                        })
                        .eq('id', vendaData.imovel_id)
                }
            }

            loadVendas()
            loadImoveis()

            // Mostrar toast de sucesso
            showSuccess(
                'Venda excluída com sucesso!',
                `A venda do imóvel ${(vendaToDelete.imovel as any)?.nome} foi excluída.`
            )

            // Fechar modal de confirmação
            setIsDeleteModalOpen(false)
            setGlobalModalOpen(false)
            setVendaToDelete(null)
        } catch (err: any) {
            setError(err.message || 'Erro ao excluir venda')
            showError(
                'Erro ao excluir venda',
                'Não foi possível excluir a venda. Tente novamente.'
            )
        }
    }

    const cancelDelete = () => {
        setIsDeleteModalOpen(false)
        setGlobalModalOpen(false)
        setVendaToDelete(null)
    }

    const openCreateModal = () => {
        setEditingVenda(null)
        setFormData({
            imovel_id: '',
            unidade: '',
            corretor_id: '',
            cliente_nome: '',
            valor_venda: '',
            data_venda: '',
            valor_comissao: '',
            valor_bonus: '',
            previsao_recebimento: '',
            observacoes: '',
            tipo_imovel: 'novo',
            tipo_id: '',
            construtora_id: '',
            proprietario_id: ''
        })
        setIsViewMode(false)
        setIsModalOpen(true)
        setGlobalModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setGlobalModalOpen(false)
        setEditingVenda(null)
        setFormData({
            imovel_id: '',
            unidade: '',
            corretor_id: '',
            cliente_nome: '',
            valor_venda: '',
            data_venda: '',
            valor_comissao: '',
            valor_bonus: '',
            previsao_recebimento: '',
            observacoes: '',
            tipo_imovel: 'novo',
            tipo_id: '',
            construtora_id: '',
            proprietario_id: ''
        })
        setError('')
        setIsViewMode(false)
    }

    // Função para abrir lightbox de comissões
    const openComissoesLightbox = (venda: Venda) => {
        setSelectedVenda(venda)
        setIsComissoesLightboxOpen(true)
        setGlobalModalOpen(true)
    }

    // Função para fechar lightbox de comissões
    const closeComissoesLightbox = () => {
        setIsComissoesLightboxOpen(false)
        setGlobalModalOpen(false)
        setSelectedVenda(null)
    }

    // Filtros
    const filteredVendas = vendas.filter(venda => {
        const matchesSearch =
            venda.imovel?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            venda.unidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            venda.corretor?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            venda.corretor?.creci?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            venda.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            venda.observacoes?.toLowerCase().includes(searchTerm.toLowerCase())

        return matchesSearch
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    const handleConstrutoraChange = (construtoraId: string) => {
        setFormData({
            ...formData,
            construtora_id: construtoraId,
            imovel_id: '', // Limpar imóvel selecionado
            proprietario_id: '' // Limpar proprietário se estava selecionado
        })
    }

    const handleProprietarioChange = (proprietarioId: string) => {
        setFormData({
            ...formData,
            proprietario_id: proprietarioId,
            imovel_id: '', // Limpar imóvel selecionado
            construtora_id: '' // Limpar construtora se estava selecionada
        })
    }

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('pt-BR')
    }

    if (loading) {
        return (
            <div className="p-4 lg:p-6 flex items-center justify-center dark:bg-gray-900 min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando vendas...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="p-4 lg:p-6 dark:bg-gray-900 min-h-screen">
            {/* Conteúdo principal com blur quando modal aberta */}
            <div className={`transition-all duration-300 ${isModalOpen || isComissoesLightboxOpen || isDeleteModalOpen ? 'blur-sm opacity-50' : ''}`}>
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Vendas</h1>
                            <p className="text-gray-500 dark:text-gray-400">Gerencie todas as vendas do sistema</p>
                        </div>
                        <Button
                            variant="btn_add"
                            onClick={openCreateModal}
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Nova Venda
                        </Button>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                    <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Total de Vendas</p>
                                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{vendas.length}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                    <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Valor Total</p>
                                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {formatCurrency(vendas.reduce((acc, venda) => acc + venda.valor_venda, 0))}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                    <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Este Mês</p>
                                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {vendas.filter(venda => {
                                            const vendaMonth = new Date(venda.data_venda).getMonth()
                                            const currentMonth = new Date().getMonth()
                                            return vendaMonth === currentMonth
                                        }).length}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Buscar por imóvel, unidade, corretor, cliente, observações..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            <p className="text-red-700 dark:text-red-400">{error}</p>
                        </div>
                    </div>
                )}

                {/* Desktop Table */}
                <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Imóvel / Unidade / Cliente
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Valor da Venda
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Comissão
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Bônus
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Data da Venda
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredVendas.map((venda) => (
                                    <tr key={venda.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                                                    <Home className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {venda.imovel?.nome || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        Unidade {venda.unidade || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                                                        {venda.cliente_nome || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-green-600 dark:text-green-400">
                                                {formatCurrency(venda.valor_venda)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-blue-600 dark:text-blue-400">
                                                {formatCurrency(venda.valor_comissao || 0)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-purple-600 dark:text-purple-400">
                                                {formatCurrency(venda.valor_bonus || 0)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-gray-100">
                                                {formatDate(venda.data_venda)}
                                            </div>
                                            {venda.previsao_recebimento && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Prev: {formatDate(venda.previsao_recebimento)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => openComissoesLightbox(venda)}
                                                    className="p-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                                    title="Gerenciar Comissões"
                                                >
                                                    <DollarSign size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(venda)}
                                                    className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(venda)}
                                                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                    title="Excluir"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-4">
                    {filteredVendas.map((venda) => (
                        <div key={venda.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            {/* Header com ícone e nome do imóvel */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center flex-1">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                                        <Home className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                            {venda.imovel?.nome || 'N/A'}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Unidade {venda.unidade || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => openComissoesLightbox(venda)}
                                        className="p-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                        title="Gerenciar Comissões"
                                    >
                                        <DollarSign size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(venda)}
                                        className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                                        title="Editar"
                                    >
                                        <Edit3 size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(venda)}
                                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                        title="Excluir"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Cliente */}
                            <div className="mb-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cliente</p>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {venda.cliente_nome || 'N/A'}
                                </p>
                            </div>

                            {/* Valores em grid */}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Valor da Venda</p>
                                    <p className="text-sm font-bold text-green-600 dark:text-green-400">
                                        {formatCurrency(venda.valor_venda)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Comissão</p>
                                    <p className="text-sm text-blue-600 dark:text-blue-400">
                                        {formatCurrency(venda.valor_comissao || 0)}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Bônus</p>
                                    <p className="text-sm text-purple-600 dark:text-purple-400">
                                        {formatCurrency(venda.valor_bonus || 0)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data da Venda</p>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {formatDate(venda.data_venda)}
                                    </p>
                                </div>
                            </div>

                            {/* Previsão de recebimento se existir */}
                            {venda.previsao_recebimento && (
                                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Previsão de Recebimento</p>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {formatDate(venda.previsao_recebimento)}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Empty State */}
                {filteredVendas.length === 0 && (
                    <div className="text-center py-12">
                        <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <DollarSign className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Nenhuma venda encontrada</h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece criando sua primeira venda'}
                        </p>
                    </div>
                )}
            </div>

            {/* Modal de Criar/Editar/Visualizar */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                {isViewMode ? 'Visualizar Venda' : editingVenda ? 'Editar Venda' : 'Nova Venda'}
                            </h2>
                            <button
                                onClick={closeModal}
                                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-2">
                                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        <p className="text-red-700 dark:text-red-400">{error}</p>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Cliente */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Nome do Cliente *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.cliente_nome}
                                        onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                                        placeholder="Digite o nome do cliente"
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        required
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Tipo de Imóvel (Casa, Apartamento, etc.) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Tipo de Imóvel *
                                    </label>
                                    <select
                                        value={formData.tipo_id}
                                        onChange={(e) => setFormData({ ...formData, tipo_id: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        required
                                        disabled={isViewMode}
                                    >
                                        <option value="">Selecione um tipo</option>
                                        {tiposImoveis.map((tipo) => (
                                            <option key={tipo.id} value={tipo.id}>
                                                {tipo.nome}
                                            </option>
                                        ))}
                                    </select>
                                    {tiposImoveis.length === 0 && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Nenhum tipo cadastrado.
                                            <a href="/imoveis" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline ml-1">
                                                Clique aqui para adicionar tipos
                                            </a>
                                        </p>
                                    )}
                                </div>

                                {/* Condição do Imóvel (Novo/Usado) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Condição do Imóvel *
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="tipo_imovel"
                                                value="novo"
                                                checked={formData.tipo_imovel === 'novo'}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    tipo_imovel: e.target.value,
                                                    construtora_id: '',
                                                    proprietario_id: ''
                                                })}
                                                className="mr-2 text-indigo-600 dark:text-indigo-400"
                                                disabled={isViewMode}
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Imóvel Novo</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="tipo_imovel"
                                                value="usado"
                                                checked={formData.tipo_imovel === 'usado'}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    tipo_imovel: e.target.value,
                                                    construtora_id: '',
                                                    proprietario_id: ''
                                                })}
                                                className="mr-2 text-indigo-600 dark:text-indigo-400"
                                                disabled={isViewMode}
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Imóvel Usado</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Construtora (apenas para imóvel novo) */}
                                {formData.tipo_imovel === 'novo' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Construtora *
                                        </label>
                                        <select
                                            value={formData.construtora_id}
                                            onChange={(e) => handleConstrutoraChange(e.target.value)}
                                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            required
                                            disabled={isViewMode}
                                        >
                                            <option value="">Selecione uma construtora</option>
                                            {construtoras.map((construtora) => (
                                                <option key={construtora.id} value={construtora.id}>
                                                    {construtora.nome}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Proprietário (apenas para imóvel usado) */}
                                {formData.tipo_imovel === 'usado' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Proprietário *
                                        </label>
                                        <select
                                            value={formData.proprietario_id}
                                            onChange={(e) => handleProprietarioChange(e.target.value)}
                                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            required
                                            disabled={isViewMode}
                                        >
                                            <option value="">Selecione um proprietário</option>
                                            {proprietarios.map((proprietario) => (
                                                <option key={proprietario.id} value={proprietario.id}>
                                                    {proprietario.nome} - {proprietario.cidade}/{proprietario.estado}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Imóvel */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Imóvel *
                                    </label>
                                    <select
                                        value={formData.imovel_id}
                                        onChange={(e) => setFormData({ ...formData, imovel_id: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        required
                                        disabled={isViewMode}
                                    >
                                        <option value="">
                                            {!formData.tipo_id
                                                ? 'Primeiro selecione o tipo de imóvel'
                                                : formData.tipo_imovel === 'novo'
                                                    ? (formData.construtora_id ? 'Selecione um imóvel' : 'Primeiro selecione uma construtora')
                                                    : (formData.proprietario_id ? 'Selecione um imóvel' : 'Primeiro selecione um proprietário')
                                            }
                                        </option>
                                        {imoveis
                                            .filter(imovel => {
                                                // Filtro por tipo de imóvel (Casa, Apartamento, etc.)
                                                const tipoCorreto = formData.tipo_id ?
                                                    tiposImoveis.find(t => t.id === formData.tipo_id)?.nome === imovel.tipo : true

                                                if (formData.tipo_imovel === 'novo') {
                                                    // Para imóvel novo: deve ser tipo 'novo' E ter construtora selecionada E tipo correto
                                                    return imovel.tipo_imovel === 'novo' &&
                                                        formData.construtora_id &&
                                                        imovel.construtora_id === formData.construtora_id &&
                                                        tipoCorreto
                                                } else {
                                                    // Para imóvel usado: deve ser tipo 'usado' E ter proprietário selecionado E tipo correto
                                                    return imovel.tipo_imovel === 'usado' &&
                                                        formData.proprietario_id &&
                                                        imovel.proprietario_id === formData.proprietario_id &&
                                                        tipoCorreto
                                                }
                                            })
                                            .map((imovel) => (
                                                <option key={imovel.id} value={imovel.id}>
                                                    {imovel.nome}
                                                </option>
                                            ))}
                                    </select>
                                    {/* Mensagem de feedback quando não há imóveis */}
                                    {(() => {
                                        const imoveisFiltrados = imoveis.filter(imovel => {
                                            // Filtro por tipo de imóvel (Casa, Apartamento, etc.)
                                            const tipoCorreto = formData.tipo_id ?
                                                tiposImoveis.find(t => t.id === formData.tipo_id)?.nome === imovel.tipo : true

                                            if (formData.tipo_imovel === 'novo') {
                                                return imovel.tipo_imovel === 'novo' &&
                                                    formData.construtora_id &&
                                                    imovel.construtora_id === formData.construtora_id &&
                                                    tipoCorreto
                                            } else {
                                                return imovel.tipo_imovel === 'usado' &&
                                                    formData.proprietario_id &&
                                                    imovel.proprietario_id === formData.proprietario_id &&
                                                    tipoCorreto
                                            }
                                        })

                                        if (formData.tipo_imovel === 'novo' && formData.construtora_id && imoveisFiltrados.length === 0) {
                                            return (
                                                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    Nenhum imóvel encontrado para esta construtora
                                                </p>
                                            )
                                        } else if (formData.tipo_imovel === 'usado' && formData.proprietario_id && imoveisFiltrados.length === 0) {
                                            return (
                                                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    Nenhum imóvel encontrado para este proprietário
                                                </p>
                                            )
                                        }
                                        return null
                                    })()}
                                </div>

                                {/* Unidade */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Unidade *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.unidade}
                                        onChange={(e) => setFormData({ ...formData, unidade: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        placeholder="Ex: apt 101 A, casa 5, loja 12"
                                        required
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Corretor - Oculto pois é automaticamente o usuário logado */}
                                <input
                                    type="hidden"
                                    name="corretor_id"
                                    value={formData.corretor_id}
                                />

                                {/* Mostrar informação do corretor apenas no modo visualização */}
                                {isViewMode && corretores.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Corretor
                                        </label>
                                        <div className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
                                            <div className="flex items-center">
                                                <User className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                                    {corretores[0].nome}
                                                </span>
                                                {corretores[0].creci && (
                                                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                                        ({corretores[0].creci})
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Valor da Venda */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Valor da Venda *
                                    </label>
                                    <CurrencyInputV2
                                        value={formData.valor_venda}
                                        onChange={(value) => setFormData({ ...formData, valor_venda: value })}
                                        placeholder="R$ 0,00"
                                        required
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Data da Venda */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Data da Venda *
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.data_venda}
                                        onChange={(e) => setFormData({ ...formData, data_venda: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        required
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Valor da Comissão */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Valor da Comissão
                                    </label>
                                    <CurrencyInputV2
                                        value={formData.valor_comissao}
                                        onChange={(value) => setFormData({ ...formData, valor_comissao: value })}
                                        placeholder="R$ 0,00"
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Valor do Bônus */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Valor do Bônus
                                    </label>
                                    <CurrencyInputV2
                                        value={formData.valor_bonus}
                                        onChange={(value) => setFormData({ ...formData, valor_bonus: value })}
                                        placeholder="R$ 0,00"
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Previsão de Recebimento */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Previsão de Recebimento
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.previsao_recebimento}
                                        onChange={(e) => setFormData({ ...formData, previsao_recebimento: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Observações */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Observações
                                    </label>
                                    <textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        rows={3}
                                        placeholder="Digite observações sobre a venda..."
                                        disabled={isViewMode}
                                    />
                                </div>
                            </div>

                            {!isViewMode && (
                                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="btn-secondary"
                                    >
                                        Cancelar
                                    </button>
                                    <Button
                                        type="submit"
                                        variant="btn_add"
                                        className="flex items-center gap-2"
                                    >
                                        <Save size={20} />
                                        {editingVenda ? 'Atualizar' : 'Criar'} Venda
                                    </Button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                            </div>
                        </div>

                        <div className="text-center mb-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                Confirmar Exclusão
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                Tem certeza que deseja excluir esta venda?
                            </p>
                            {vendaToDelete && (
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4 text-left">
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        <strong>Imóvel:</strong> {(vendaToDelete.imovel as any)?.nome}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        <strong>Unidade:</strong> {vendaToDelete.unidade}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        <strong>Cliente:</strong> {vendaToDelete.cliente_nome}
                                    </p>
                                </div>
                            )}
                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                                ⚠️ Esta operação não pode ser revertida
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={cancelDelete}
                                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors font-medium"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox de Comissões */}
            <ComissoesLightbox
                venda={selectedVenda}
                isOpen={isComissoesLightboxOpen}
                onClose={closeComissoesLightbox}
            />

            {/* Toast Container */}
            <ToastContainer toasts={toasts} />
        </div>
    )
}
